import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLSelectElement} [variantSelect] - Sticky variant dropdown
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#resetButtonState();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();
    document.body.classList.remove('has-sticky-atc-visible');
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatPanelOpen()) return;
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Scrolls to the first option still missing (pack, color, extra batteries).
   */
  handleAddToCartClick = () => {
    const target = this.#missingChoiceTarget();
    this.#scrollToChoice(target);
  };

  #missingChoiceTarget() {
    const scope = document.querySelector('.product-information') || document;

    const pack = scope.querySelector('rc-pack-picker');
    if (pack && !pack.querySelector('.rc-pack-picker__radio:checked')) {
      return pack.querySelector('.rc-pack-picker__legend') || pack;
    }

    const color = scope.querySelector('.variant-option--buttons:not(.rc-color-clone)');
    if (color && !color.querySelector('input:checked')) {
      return color.querySelector('legend') || color;
    }

    const extra = scope.querySelector('.rc-extra-batteries');
    if (extra && !extra.querySelector('input:checked, .is-selected')) {
      return extra.querySelector('.rc-extra-batteries__head') || extra;
    }

    return pack || color || extra || scope.querySelector('variant-picker') || scope.querySelector('.buy-buttons-block');
  }

  #scrollRoot() {
    const wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return window;

    const overflowY = window.getComputedStyle(wrapper).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return wrapper;
    return window;
  }

  #stickyHeaderOffset() {
    let bottom = 16;

    ['.announcement-marquee', '.rc-main-nav', '.rc-category-nav'].forEach((selector) => {
      const element = document.querySelector(selector);
      if (!element) return;

      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return;

      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > bottom) {
        bottom = rect.bottom;
      }
    });

    return bottom + 12;
  }

  #scrollToChoice(element) {
    if (!element) return;

    const root = this.#scrollRoot();
    const offset = this.#stickyHeaderOffset();
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReduced ? 'auto' : 'smooth';

    if (root === window) {
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior });
      return;
    }

    const top = element.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - offset;
    root.scrollTo({ top: Math.max(0, top), behavior });
  }

  /**
   * Restores button state after bfcache navigation.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    this.#resetButtonState();
  };

  /**
   * Ensures sticky CTA text is always visible.
   */
  #resetButtonState() {
    this.refs.addToCartButton?.removeAttribute('data-added');
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        this.#syncVariantSelect();
        this.#resetButtonState();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Handles variant selection from the sticky dropdown.
   * @param {Event} event
   */
  handleVariantSelectChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    this.#selectVariantById(event.target.value);
  };

  /**
   * Selects a variant in the main product form pickers.
   * @param {string} variantId
   */
  #selectVariantById(variantId) {
    const productId = this.dataset.productId;
    if (!productId || !variantId) return;

    const pickers = document.querySelectorAll(`variant-picker[data-product-id="${productId}"]`);
    if (!pickers.length) return;

    for (const picker of pickers) {
      const input = picker.querySelector(`input[type="radio"][data-variant-id="${variantId}"]`);
      if (input instanceof HTMLInputElement && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    const variantsJson = this.querySelector('[data-variants-json]');
    if (!variantsJson?.textContent) return;

    let variants;
    try {
      variants = JSON.parse(variantsJson.textContent);
    } catch {
      return;
    }

    const variant = variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);

    for (const picker of pickers) {
      for (const optionValue of optionValues) {
        const radio = picker.querySelector(`input[type="radio"][value="${CSS.escape(optionValue)}"]`);
        if (radio instanceof HTMLInputElement && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }

        const select = picker.querySelector('select.variant-option__select');
        if (select instanceof HTMLSelectElement) {
          const hasOption = Array.from(select.options).some((option) => option.value === optionValue);
          if (hasOption && select.value !== optionValue) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    }
  }

  /**
   * Syncs sticky variant dropdown with the current variant.
   */
  #syncVariantSelect() {
    const { variantSelect } = this.refs;
    const variantId = this.dataset.currentVariantId;
    if (!(variantSelect instanceof HTMLSelectElement) || !variantId) return;

    const option = Array.from(variantSelect.options).find((item) => item.value === String(variantId));
    if (option && !option.disabled) {
      variantSelect.value = String(variantId);
    }
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    document.body.classList.add('has-sticky-atc-visible');
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    document.body.classList.remove('has-sticky-atc-visible');
  }

  // Helper methods
  /**
   * True only when the chat conversation panel is open.
   * The mobile FAB must not hide the sticky add-to-cart bar.
   * @returns {boolean}
   */
  #isChatPanelOpen() {
    const chat = document.querySelector('shopify-chat');
    return Boolean(chat?.hasAttribute('open'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
